// ============================================================================
// GROUPING WRAPPER - GROUPING CONTROLS COMPONENT
// ============================================================================

import React, { useState, ChangeEvent } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import type { GroupingControlsProps, SelectFieldProps } from "./types";

const getFieldIcon = (type: string): string => {
  switch (type) {
    case "select":
    case "client-select":
      return "🏷️";
    case "boolean":
      return "✓";
    case "date":
    case "datetime":
      return "📅";
    case "number":
      return "🔢";
    case "multi-user":
      return "👥";
    case "tags":
      return "🏷️";
    default:
      return "📝";
  }
};

const SelectField: React.FC<SelectFieldProps> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => (
  <div className="relative flex-1">
    <select
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
      className="w-full px-3 py-2.5 pl-9 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-card focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none cursor-pointer hover:border-gray-400 disabled:bg-card-100 disabled:text-gray-500 disabled:cursor-not-allowed"
    >
      <option value="">{placeholder}</option>
      {options.map((field) => (
        <option key={field.fieldId} value={field.fieldId}>
          {getFieldIcon(field.type)} {field.label}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
  </div>
);

export const GroupingControls: React.FC<GroupingControlsProps> = ({
  groupableFields,
  groupByField,
  onGroupByFieldChange,
  dualGrouping,
  onDualGroupingChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (groupableFields.length === 0) {
    return null;
  }

  const primaryField = dualGrouping.primary || groupByField;
  const secondaryField = dualGrouping.secondary;

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      {/* Collapsible Header */}
      <button type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Grouping Options</h3>
          {(primaryField || secondaryField) && !isExpanded && (
            <span className="text-xs text-gray-500 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-full">
              {secondaryField ? "2 levels" : "1 level"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(primaryField || secondaryField) && (
            <button type="button"
              onClick={(e) => {
                e.stopPropagation();
                onGroupByFieldChange(null);
                onDualGroupingChange(null, null);
              }}
              className="text-xs px-2.5 py-1 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 transition-colors font-medium"
            >
              Clear All
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 space-y-3">
          {/* Primary Grouping */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold dark:bg-blue-900/40 dark:text-blue-300">
                1
              </span>
              Primary:
            </label>
            <div className="flex-1 flex items-center gap-2">
              <SelectField
                value={primaryField}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const value = e.target.value || null;
                  if (dualGrouping.primary) {
                    onDualGroupingChange(value, dualGrouping.secondary);
                  } else {
                    onGroupByFieldChange(value);
                  }
                }}
                options={groupableFields}
                placeholder="Select field..."
              />
              {primaryField && (
                <button type="button"
                  onClick={() => {
                    onGroupByFieldChange(null);
                    onDualGroupingChange(null, null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-card-100 rounded-lg transition-colors flex-shrink-0"
                  title="Clear primary grouping"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Secondary Grouping */}
          {primaryField && (
            <div className="flex items-center gap-3 pl-4 border-l-2 border-blue-300 bg-blue-50/50 py-2 px-3 rounded-r-lg dark:border-blue-800 dark:bg-blue-950/40">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit flex items-center gap-1.5">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold dark:bg-indigo-900/40 dark:text-indigo-300">
                  2
                </span>
                Secondary:
              </label>
              <div className="flex-1 flex items-center gap-2">
                <SelectField
                  value={secondaryField}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    const value = e.target.value || null;
                    onDualGroupingChange(primaryField, value);
                  }}
                  options={groupableFields.filter((f) => f.fieldId !== primaryField)}
                  placeholder="None"
                />
                {secondaryField && (
                  <button type="button"
                    onClick={() => onDualGroupingChange(primaryField, null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-card-100 rounded-lg transition-colors flex-shrink-0"
                    title="Clear secondary grouping"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Info Text */}
          {!primaryField && (
            <p className="text-xs text-gray-500 italic pl-1">Select a field to group data by</p>
          )}
          {primaryField && !secondaryField && (
            <p className="text-xs text-gray-500 italic pl-1">
              Optionally select a secondary field for nested grouping
            </p>
          )}
          {primaryField && secondaryField && (
            <p className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg font-medium flex items-center gap-2 dark:text-green-300 dark:bg-green-950/40">
              <span className="text-sm">✓</span>
              Grouped by{" "}
              <span className="font-semibold">
                {groupableFields.find((f) => f.fieldId === primaryField)?.label}
              </span>
              <span className="text-gray-400">→</span>
              <span className="font-semibold">
                {groupableFields.find((f) => f.fieldId === secondaryField)?.label}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
};
